/**
 * Multi-unit Large UPS Summary display (#997 / #996 / Story 4.3) —
 * descriptor-engine-driven rows via a thin shim so Summary consumers'
 * `getPerLargeUpsSummaryDefinitions` / `largeUpsSummaryUnitRowValue`
 * imports are undisturbed.
 */

import {
  LARGE_UPS_EDITOR_SUBSECTION_FIELDS,
  LARGE_UPS_PER_INSTANCE_EDITOR_FIELD_KEYS,
} from '../request/fields/requestFieldRegistry';
import {
  getLargeUpsDescriptorSummaryDefinitions,
  getLargeUpsDescriptorUnitTitle,
  largeUpsDescriptorUnitRowValue,
} from '../request/sections/largeUpsDescriptorSummary';
import { countSubsectionFields } from '../utils/subsectionHeaderMetrics';

import type { SummaryCardRow } from './summaryCardSourceTypes';
import type { LargeUpsConfiguration } from '../types/largeUps';

export {
  LARGE_UPS_EDITOR_SUBSECTION_FIELDS,
  LARGE_UPS_PER_INSTANCE_EDITOR_FIELD_KEYS as LARGE_UPS_PER_INSTANCE_FIELD_KEYS,
};

/**
 * Per-unit Summary rows for one Large UPS — engine-driven source
 * (Story 4.3). Byte-identical to the pre-engine bespoke row set (parity
 * oracle `large-ups-summary-rows.json`).
 */
export function getPerLargeUpsSummaryDefinitions(): SummaryCardRow[] {
  return getLargeUpsDescriptorSummaryDefinitions();
}

export function resolveSummaryLargeUpsConfigurations(input: {
  sessionUnits?: LargeUpsConfiguration[] | null;
  draftUnits?: LargeUpsConfiguration[] | null;
  persistedUnits?: LargeUpsConfiguration[] | null;
  /** #997 C3: true when edited this session — an empty edited array is authoritative (no delete-all resurrection). */
  edited?: boolean;
}): LargeUpsConfiguration[] {
  const { sessionUnits, draftUnits, persistedUnits, edited } = input;
  if (sessionUnits && sessionUnits.length > 0) {
    return sessionUnits;
  }
  if (edited) {
    return draftUnits ?? [];
  }
  if (draftUnits && draftUnits.length > 0) {
    return draftUnits;
  }
  if (persistedUnits && persistedUnits.length > 0) {
    return persistedUnits;
  }
  return [];
}

export function largeUpsSummaryUnitRowValue(
  unit: LargeUpsConfiguration,
  dataField: string,
): string | number | boolean | null | undefined {
  return largeUpsDescriptorUnitRowValue(unit, dataField);
}

/**
 * Per-unit Summary card title — descriptor-driven (mirrors the LC shim
 * `getLiquidCoolingCduSummaryUnitTitle` and the Racks shim
 * `getRackSummaryUnitTitle`). Story 4.3 golden parity oracle: the Summary
 * golden test pins the production title output, not a local helper.
 */
export function getLargeUpsSummaryUnitTitle(
  unit: LargeUpsConfiguration,
  index: number,
): string {
  return getLargeUpsDescriptorUnitTitle(unit, index);
}

export function countFilledLargeUpsSummaryFields(
  unit: LargeUpsConfiguration,
  rows?: readonly SummaryCardRow[],
): number {
  const fieldKeys = new Set(
    (rows ?? getPerLargeUpsSummaryDefinitions()).map(row => row.dataField),
  );
  const fields = LARGE_UPS_EDITOR_SUBSECTION_FIELDS.filter(field =>
    fieldKeys.has(field.key),
  );
  return countSubsectionFields(
    fields,
    unit as unknown as Record<string, unknown>,
  ).filled;
}

export function countFilledLargeUpsSummaryFieldsForUnits(
  units: readonly LargeUpsConfiguration[],
  rows: readonly SummaryCardRow[],
): number {
  return units.reduce(
    (sum, unit) => sum + countFilledLargeUpsSummaryFields(unit, rows),
    0,
  );
}
